import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import {
  Container,
  Header,
  Title,
  StyledList,
  ListItemContainer,
  CustomEditIcon,
  CustomRefreshIcon,
  IconSpace,
  ScrollContainer,
  CustomConfirmIcon,
  CustomCancelIcon,
  ExtraReports,
} from "./styles";
import ListItem from "./ListItem";
import { updateActiveReports } from "../../../state/DnDSlice/slice";
import {
  fetchDashboardFlows,
  fetchDashboardTasks,
  fetchDashboardUsers,
} from "../../../state/dashboard/actions";
import { debounce } from "lodash";

const DeDList = () => {
  const dispatch = useDispatch();
  const reports = useSelector((state) => state.dnd.items);
  const [isEditing, setIsEditing] = useState(false);
  const [availableReports, setAvailableReports] = useState([]);
  const [currentReports, setCurrentReports] = useState([]);

  const dashUsers = useSelector((state) => state.dashboard.usersProductivity);
  const dashTasks = useSelector((state) => state.dashboard.tasksProductivity);
  const dashFlows = useSelector((state) => state.dashboard.flowsProductivity);

  const loading = dashUsers.status === "loading" || dashTasks.status === "loading" || dashFlows.status === "loading" 




  const onDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return; // Check if the item is dropped outside the list

    const newCurrentReports = Array.from(currentReports); // Clone the current list for modification

    if (
      source.droppableId === "availableReports" &&
      destination.droppableId === "activeReports"
    ) {
      // Moving from available to active
      const itemToAdd = availableReports[source.index];
      newCurrentReports.splice(destination.index, 0, itemToAdd); // Add to specific index
      setAvailableReports((prev) =>
        prev.filter((_, idx) => idx !== source.index)
      ); // Remove from available
    } else if (
      source.droppableId === "activeReports" &&
      destination.droppableId === "activeReports"
    ) {
      // Reordering within active
      const [reorderedItem] = newCurrentReports.splice(source.index, 1);
      newCurrentReports.splice(destination.index, 0, reorderedItem);
    } else if (
      source.droppableId === "activeReports" &&
      destination.droppableId === "availableReports"
    ) {
      // Moving from active to available
      const itemToRemove = newCurrentReports[source.index];
      newCurrentReports.splice(source.index, 1); // Remove from active
      setAvailableReports((prev) => [...prev, itemToRemove]); // Add back to available
    }
    setCurrentReports(newCurrentReports);
  };

  const handleConfirm = () => {
    // Commit the local `currentReports` to the Redux store
    dispatch(updateActiveReports(currentReports));
    setIsEditing(false);
  };

  const handleCancel = () => {
    // Revert local changes by resetting `currentReports` to the last saved state from Redux
    handleSetReports();
    setIsEditing(false);
  };

  const handleAddReport = (report, index) => {
    const newAvailableReports = [...availableReports];
    newAvailableReports.splice(index, 1); // Remove from available
    setAvailableReports(newAvailableReports);

    setCurrentReports([...currentReports, report]); // Add to current
  };

  const handleRemoveReport = (report, index) => {
    const newCurrentReports = [...currentReports];
    newCurrentReports.splice(index, 1); // Remove from current
    setCurrentReports(newCurrentReports);

    setAvailableReports([...availableReports, report]); // Add back to available
  };

  const allPossibleReports = [
    { id: "1", type: "fluxos" },
    { id: "2", type: "tarefas" },
    { id: "3", type: "colaboradores" },
    { id: "4", type: "ChatReport" },
  ];

  const handleSetReports = () => {
    const currentIds = new Set(reports.map((item) => item.id));
    setAvailableReports(
      allPossibleReports.filter((report) => !currentIds.has(report.id))
    );
    setCurrentReports(reports);
  };

  useEffect(() => {
    handleSetReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reports]);

  const handleDebouncedRefresh = debounce(() => {
    dispatch(fetchDashboardFlows());
    dispatch(fetchDashboardUsers());
    dispatch(fetchDashboardTasks());
  }, 400);

  return (
    <Container>
      <Header>
        <Title>Relatórios</Title>
        <div>
          <IconSpace>
            {isEditing ? (
              <>
                <CustomConfirmIcon
                  onClick={handleConfirm}
                  style={{
                    backgroundColor: "#c6c7c8",
                    fontSize: 40,
                    padding: 0,
                  }}
                />
                <CustomCancelIcon
                  onClick={handleCancel}
                  style={{
                    backgroundColor: "#c6c7c8",
                    fontSize: 40,
                    padding: 0,
                  }}
                />
              </>
            ) : (
              <>
                <CustomEditIcon
                  onClick={() => setIsEditing(true)}
                  style={{ backgroundColor: "#c6c7c8", color: "#585e66" }}
                />
                <CustomRefreshIcon
                  onClick={ loading ? () => {} : handleDebouncedRefresh}
                  style={{ backgroundColor: "#c6c7c8", color: "#585e66", opacity: loading ? 0.5 : 1 }}
                />
              </>
            )}
          </IconSpace>
        </div>
      </Header>
      <ScrollContainer>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="activeReports">
            {(provided) => (
              <StyledList {...provided.droppableProps} ref={provided.innerRef}>
                {currentReports.map((item, index) => (
                  <Draggable
                    key={item.id}
                    draggableId={item.id.toString()}
                    index={index}
                  >
                    {(provided) => (
                      <ListItemContainer
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        ref={provided.innerRef}
                      >
                        <ListItem
                          item={item}
                          isEditing={isEditing}
                          remove={() => handleRemoveReport(item, index)}
                          restore={() => handleAddReport(item, index)}
                          isExcluded={false}
                        />
                      </ListItemContainer>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </StyledList>
            )}
          </Droppable>
          {isEditing ? (
            <ExtraReports>
              <Droppable droppableId="availableReports">
                {(provided) => (
                  <StyledList
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                  >
                    {availableReports.map((report, index) => (
                      <Draggable
                        key={report.id}
                        draggableId={report.id.toString()}
                        index={index}
                      >
                        {(provided) => (
                          <ListItemContainer
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            ref={provided.innerRef}
                          >
                            <ListItem
                              item={report}
                              isEditing={isEditing}
                              restore={() => handleAddReport(report, index)}
                              remove={() => handleRemoveReport(report, index)}
                              isExcluded={true}
                            />
                          </ListItemContainer>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </StyledList>
                )}
              </Droppable>
            </ExtraReports>
          ) : null}
        </DragDropContext>
      </ScrollContainer>
    </Container>
  );
};

export default DeDList;
